import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  ContentCopy as CopyIcon,
  PictureAsPdf as PdfIcon,
  Autorenew as AutorenewIcon,
  Warning as WarningIcon,
  CheckCircle as VerifiedIcon,
  VpnKey as VpnKeyIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  getOnsiteStudents,
  deleteOnsiteStudent,
  bulkDeleteOnsiteStudents,
  getOnsiteStudentChallans,
  generateOnsiteChallan,
  deleteOnsiteChallan,
  bulkDeleteOnsiteChallans,
  changeOnsiteUserPassword,
  updateOnsiteUser,
} from '../services/api';


const USER_BASE_URL =
  process.env.REACT_APP_USER_API_URL ||
  "https://backend.hunarmandpunjab.org.pk";

interface OnsiteStudent {
  _id: string;
  fullName: string;
  fatherName: string;
  cnic: string;
  phone: string;
  email: string;
  city: string;
  qualification: string;
  courseApplied: string;
  challanId?: string;
  admitCardId?: string;
  psid?: string;
  rollNumber?: string;
  registrationType?: string;
  registeredAt?: string;
  createdAt?: string;
}

function OnsiteUsers() {
  const [students, setStudents] = useState<OnsiteStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [courseApplied, setCourseApplied] = useState('');
  const [city, setCity] = useState('');
  const [qualification, setQualification] = useState('');
  const [gender, setGender] = useState('');
  const [challanPaid, setChallanPaid] = useState('');
  const [noChallan, setNoChallan] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [specificDate, setSpecificDate] = useState('');

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedStudentToDelete, setSelectedStudentToDelete] = useState<OnsiteStudent | null>(null);
  const [deletingStudent, setDeletingStudent] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");

  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [bulkDeleteReason, setBulkDeleteReason] = useState("");

  // Manage Challans states
  const [challansModalOpen, setChallansModalOpen] = useState(false);
  const [selectedStudentForChallans, setSelectedStudentForChallans] = useState<OnsiteStudent | null>(null);
  const [userChallans, setUserChallans] = useState<any[]>([]);
  const [loadingChallans, setLoadingChallans] = useState(false);
  const [challansError, setChallansError] = useState("");
  const [selectedChallanIds, setSelectedChallanIds] = useState<string[]>([]);
  const [deletingChallan, setDeletingChallan] = useState(false);
  const [generatingChallan, setGeneratingChallan] = useState(false);
  
  // Single challan delete confirmation
  const [deleteChallanConfirmOpen, setDeleteChallanConfirmOpen] = useState(false);
  const [selectedChallanToDelete, setSelectedChallanToDelete] = useState<any | null>(null);

  // Change password states
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [changePasswordReason, setChangePasswordReason] = useState("");
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [selectedStudentForAction, setSelectedStudentForAction] = useState<OnsiteStudent | null>(null);

  // Edit Name states
  const [editNameModalOpen, setEditNameModalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [editNameReason, setEditNameReason] = useState("");
  const [nameChangeLoading, setNameChangeLoading] = useState(false);


  useEffect(() => {
    fetchStudents();
    setSelectedIds([]);
  }, [
    page,
    rowsPerPage,
    search,
    courseApplied,
    city,
    qualification,
    gender,
    challanPaid,
    noChallan,
    startDate,
    endDate,
    specificDate,
  ]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelectedIds = students.map((student) => student._id);
      setSelectedIds(newSelectedIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelectedIds: string[] = [];

    if (selectedIndex === -1) {
      newSelectedIds = [...selectedIds, id];
    } else {
      newSelectedIds = selectedIds.filter((item) => item !== id);
    }

    setSelectedIds(newSelectedIds);
  };

  const handleDeleteStudent = (student: OnsiteStudent) => {
    setSelectedStudentToDelete(student);
    setDeleteReason("");
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteStudent = async () => {
    if (!selectedStudentToDelete) return;
    setDeletingStudent(true);
    try {
      await deleteOnsiteStudent(selectedStudentToDelete._id);
      alert("Onsite student and linked challan deleted successfully");
      setStudents((prev) => prev.filter((s) => s._id !== selectedStudentToDelete._id));
      setTotal((prev) => Math.max(prev - 1, 0));
      setDeleteConfirmOpen(false);
      setSelectedStudentToDelete(null);
    } catch (err: any) {
      console.error("Error deleting student:", err);
      alert(err.response?.data?.message || "Failed to delete onsite student");
    } finally {
      setDeletingStudent(false);
    }
  };

  const handleBulkDelete = () => {
    setBulkDeleteReason("");
    setBulkDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setDeletingBulk(true);
    try {
      await bulkDeleteOnsiteStudents(selectedIds);
      alert(`${selectedIds.length} onsite students deleted successfully`);
      setStudents((prev) => prev.filter((s) => !selectedIds.includes(s._id)));
      setTotal((prev) => Math.max(prev - selectedIds.length, 0));
      setSelectedIds([]);
      setBulkDeleteConfirmOpen(false);
    } catch (err: any) {
      console.error("Error bulk deleting students:", err);
      alert(err.response?.data?.message || "Failed to bulk delete onsite students");
    } finally {
      setDeletingBulk(false);
    }
  };

  const handleChangePassword = (student: OnsiteStudent) => {
    setSelectedStudentForAction(student);
    setChangePasswordModalOpen(true);
  };

  const handleEditName = (student: OnsiteStudent) => {
    setSelectedStudentForAction(student);
    setNewName(student.fullName || "");
    setEditNameReason("");
    setEditNameModalOpen(true);
  };

  const confirmEditName = async () => {
    if (!newName.trim()) {
      alert("Name cannot be empty");
      return;
    }

    if (!editNameReason.trim()) {
      alert("Please provide a reason for editing the name");
      return;
    }

    if (!selectedStudentForAction) {
      alert("No student selected");
      return;
    }

    try {
      setNameChangeLoading(true);
      await updateOnsiteUser(selectedStudentForAction._id, {
        fullName: newName.trim(),
        reason: editNameReason.trim(),
      });

      // Update local state
      setStudents((prevStudents) =>
        prevStudents.map((s) =>
          s._id === selectedStudentForAction._id ? { ...s, fullName: newName.trim() } : s
        )
      );

      // Clear state and close modal
      setSelectedStudentForAction(null);
      setEditNameModalOpen(false);
      setNewName("");
      setEditNameReason("");

      alert("Onsite student name updated successfully");
    } catch (error: any) {
      console.error("Error editing name:", error);
      alert(
        `Failed to edit name: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setNameChangeLoading(false);
    }
  };

  const confirmChangePassword = async () => {
    if (!newPassword.trim() || newPassword.length < 8) {
      alert("Password must be at least 8 characters long");
      return;
    }

    if (!changePasswordReason.trim()) {
      alert("Please provide a reason for changing the password");
      return;
    }

    if (!selectedStudentForAction) {
      alert("No student selected");
      return;
    }

    try {
      setPasswordChangeLoading(true);
      await changeOnsiteUserPassword(selectedStudentForAction._id, newPassword, changePasswordReason);
      
      // Clear state and close modal
      setSelectedStudentForAction(null);
      setChangePasswordModalOpen(false);
      setNewPassword("");
      setChangePasswordReason("");

      alert("Onsite user password updated successfully");
    } catch (error: any) {
      console.error("Error changing password:", error);
      alert(
        `Failed to change password: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const handleManageChallans = async (student: OnsiteStudent) => {
    setSelectedStudentForChallans(student);
    setUserChallans([]);
    setChallansError("");
    setSelectedChallanIds([]);
    setChallansModalOpen(true);
    setLoadingChallans(true);
    try {
      const response = await getOnsiteStudentChallans(student._id);
      if (response.success) {
        setUserChallans(response.challans || []);
      } else {
        setChallansError("Failed to retrieve challans data");
      }
    } catch (err: any) {
      console.error("Error fetching student challans:", err);
      setChallansError(err.response?.data?.message || "Failed to load challans");
    } finally {
      setLoadingChallans(false);
    }
  };

  const handleChallanSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelectedIds = userChallans.map((c) => c._id);
      setSelectedChallanIds(newSelectedIds);
    } else {
      setSelectedChallanIds([]);
    }
  };

  const handleChallanSelectOne = (id: string) => {
    const selectedIndex = selectedChallanIds.indexOf(id);
    let newSelectedIds: string[] = [];

    if (selectedIndex === -1) {
      newSelectedIds = [...selectedChallanIds, id];
    } else if (selectedIndex === 0) {
      newSelectedIds = [...selectedChallanIds.slice(1)];
    } else if (selectedIndex === selectedChallanIds.length - 1) {
      newSelectedIds = [...selectedChallanIds.slice(0, -1)];
    } else if (selectedIndex > 0) {
      newSelectedIds = [
        ...selectedChallanIds.slice(0, selectedIndex),
        ...selectedChallanIds.slice(selectedIndex + 1),
      ];
    }
    setSelectedChallanIds(newSelectedIds);
  };

  const handleGenerateOnsiteChallan = async () => {
    if (!selectedStudentForChallans) return;
    setGeneratingChallan(true);
    try {
      const response = await generateOnsiteChallan(selectedStudentForChallans._id);
      if (response.success) {
        alert("Challan generated successfully");
        handleManageChallans(selectedStudentForChallans);
        fetchStudents();
      } else {
        alert(response.message || "Failed to generate challan");
      }
    } catch (err: any) {
      console.error("Error generating challan:", err);
      alert(err.response?.data?.message || "Failed to generate challan");
    } finally {
      setGeneratingChallan(false);
    }
  };

  const handleOpenDeleteChallan = (challan: any) => {
    setSelectedChallanToDelete(challan);
    setDeleteChallanConfirmOpen(true);
  };

  const handleDeleteChallanConfirm = async () => {
    if (!selectedChallanToDelete) return;
    setDeletingChallan(true);
    try {
      await deleteOnsiteChallan(selectedChallanToDelete._id);
      alert("Challan deleted successfully");
      setUserChallans((prev) => prev.filter((c) => c._id !== selectedChallanToDelete._id));
      setSelectedChallanIds((prev) => prev.filter((id) => id !== selectedChallanToDelete._id));
      setDeleteChallanConfirmOpen(false);
      setSelectedChallanToDelete(null);
      fetchStudents();
    } catch (err: any) {
      console.error("Error deleting challan:", err);
      alert(err.response?.data?.message || "Failed to delete challan");
    } finally {
      setDeletingChallan(false);
    }
  };

  const handleBulkDeleteChallans = async () => {
    if (selectedChallanIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedChallanIds.length} selected challans?`)) return;
    setDeletingChallan(true);
    try {
      await bulkDeleteOnsiteChallans(selectedChallanIds);
      alert(`${selectedChallanIds.length} challans deleted successfully`);
      setUserChallans((prev) => prev.filter((c) => !selectedChallanIds.includes(c._id)));
      setSelectedChallanIds([]);
      fetchStudents();
    } catch (err: any) {
      console.error("Error bulk deleting challans:", err);
      alert(err.response?.data?.message || "Failed to bulk delete challans");
    } finally {
      setDeletingChallan(false);
    }
  };

  const handleCopyChallanLink = (challan: any) => {
    const fullLink = `${USER_BASE_URL}/api/onsite/challan/${challan.challanId}/pdf?view=true`;
    navigator.clipboard.writeText(fullLink)
      .then(() => {
        alert(`Challan link copied to clipboard:\n${fullLink}`);
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        alert("Failed to copy link to clipboard");
      });
  };

  const handleViewChallanPDF = (challan: any) => {
    const fullLink = `${USER_BASE_URL}/api/onsite/challan/${challan.challanId}/pdf?view=true`;
    window.open(fullLink, "_blank");
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const data = await getOnsiteStudents(
        page + 1,
        rowsPerPage,
        search || undefined,
        courseApplied || undefined,
        city || undefined,
        qualification || undefined,
        gender || undefined,
        challanPaid || undefined,
        noChallan || undefined,
        specificDate ? specificDate : (startDate || undefined),
        specificDate ? specificDate : (endDate || undefined)
      );
      setStudents(data.students || []);
      setTotal(data.pagination?.total || 0);
      setError('');
    } catch (err) {
      console.error('Error fetching onsite students:', err);
      setError('Unable to load onsite users.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setSearch('');
    setCourseApplied('');
    setCity('');
    setQualification('');
    setGender('');
    setChallanPaid('');
    setNoChallan('');
    setStartDate('');
    setEndDate('');
    setSpecificDate('');
    setPage(0);
  };

  const activeFilters = [
    search,
    courseApplied,
    city,
    qualification,
    gender,
    challanPaid,
    noChallan,
    startDate,
    endDate,
    specificDate,
  ].filter((value) => value && value.length > 0).length;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Onsite Users
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
        <Typography variant="subtitle1" gutterBottom>
          Review onsite registered applicants and their challan/admit-card details.
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
          <Paper sx={{ p: 2, minWidth: 200, flex: 1 }} elevation={0}>
            <Typography color="textSecondary" gutterBottom>
              Total Users
            </Typography>
            <Typography variant="h5">{total.toLocaleString()}</Typography>
            <Typography variant="body2" color="text.secondary">
              Registered onsite users
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 200, flex: 1 }} elevation={0}>
            <Typography color="textSecondary" gutterBottom>
              Current Page
            </Typography>
            <Typography variant="h5">{page + 1}</Typography>
            <Typography variant="body2" color="text.secondary">
              Showing {rowsPerPage} rows per page
            </Typography>
          </Paper>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            {activeFilters > 0 && (
              <Chip label={`${activeFilters} active`} size="small" sx={{ ml: 1 }} />
            )}
          </Box>
          {activeFilters > 0 && (
            <IconButton onClick={clearFilters} title="Clear Filters">
              <CancelIcon />
            </IconButton>
          )}
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            label="Search"
            placeholder="Name, CNIC, phone, email"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <TextField
            label="Course"
            value={courseApplied}
            onChange={(e) => {
              setCourseApplied(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Gender</InputLabel>
            <Select
              value={gender}
              label="Gender"
              onChange={(e) => {
                setGender(e.target.value as string);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Challan Paid</InputLabel>
            <Select
              value={challanPaid}
              label="Challan Paid"
              onChange={(e) => {
                setChallanPaid(e.target.value as string);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="false">Unpaid</MenuItem>
              <MenuItem value="true">Paid</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Challan Status</InputLabel>
            <Select
              value={noChallan}
              label="Challan Status"
              onChange={(e) => {
                setNoChallan(e.target.value as string);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">No Challan</MenuItem>
              <MenuItem value="false">Has Challan</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="City"
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Qualification"
            value={qualification}
            onChange={(e) => {
              setQualification(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Filter by Date"
            type="date"
            value={specificDate}
            onChange={(e) => {
              setSpecificDate(e.target.value);
              setStartDate('');
              setEndDate('');
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="From Date"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setSpecificDate('');
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            disabled={!!specificDate}
          />
          <TextField
            label="To Date"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setSpecificDate('');
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            disabled={!!specificDate}
          />
        </Box>
      </Paper>

      {selectedIds.length > 0 && (
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: '#ffebee', borderRadius: 1 }}>
          <Typography color="error" variant="subtitle1" fontWeight="600">
            {selectedIds.length} users selected
          </Typography>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
          >
            Delete Selected
          </Button>
        </Box>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < students.length}
                    checked={students.length > 0 && selectedIds.length === students.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>CNIC</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>Qualification</TableCell>
                <TableCell>Admit Card</TableCell>
                <TableCell>Challan</TableCell>
                <TableCell>PSID</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : students.length ? (
                students.map((student) => {
                  const isItemSelected = selectedIds.indexOf(student._id) !== -1;
                  return (
                    <TableRow key={student._id} hover selected={isItemSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={() => handleSelectOne(student._id)}
                        />
                      </TableCell>
                      <TableCell>{student.fullName}</TableCell>
                      <TableCell>{student.cnic}</TableCell>
                      <TableCell>{student.phone}</TableCell>
                      <TableCell>{student.email}</TableCell>
                      <TableCell>{student.city}</TableCell>
                      <TableCell>{student.courseApplied}</TableCell>
                      <TableCell>{student.qualification}</TableCell>
                      <TableCell>{student.admitCardId || '-'}</TableCell>
                      <TableCell>{student.challanId || '-'}</TableCell>
                      <TableCell>{student.psid || '-'}</TableCell>
                      <TableCell>
                        {student.registeredAt
                          ? new Date(student.registeredAt).toLocaleDateString()
                          : student.createdAt
                          ? new Date(student.createdAt).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <Tooltip title="Manage Challans">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleManageChallans(student)}
                            >
                              <ReceiptIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Name">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleEditName(student)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Change Password">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleChangePassword(student)}
                            >
                              <VpnKeyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Onsite Student">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteStudent(student)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    No onsite users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deletingStudent && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Delete Onsite Student
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete onsite student <strong>{selectedStudentToDelete?.fullName}</strong>?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1, mb: 2 }}>
              This will permanently delete their student record and also delete all associated onsite challans.
            </Typography>
            <TextField
              fullWidth
              label="Reason for deletion"
              placeholder="Provide a reason..."
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              multiline
              rows={2}
              required
              disabled={deletingStudent}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deletingStudent}>Cancel</Button>
          <Button
            onClick={confirmDeleteStudent}
            color="error"
            variant="contained"
            disabled={!deleteReason.trim() || deletingStudent}
            startIcon={deletingStudent ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingStudent ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        open={bulkDeleteConfirmOpen}
        onClose={() => !deletingBulk && setBulkDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Bulk Delete Onsite Students
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete <strong>{selectedIds.length}</strong> selected onsite students?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1, mb: 2 }}>
              This will permanently delete the selected students and their associated challans.
            </Typography>
            <TextField
              fullWidth
              label="Reason for deletion"
              placeholder="Provide a reason..."
              value={bulkDeleteReason}
              onChange={(e) => setBulkDeleteReason(e.target.value)}
              multiline
              rows={2}
              required
              disabled={deletingBulk}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteConfirmOpen(false)} disabled={deletingBulk}>Cancel</Button>
          <Button
            onClick={confirmBulkDelete}
            color="error"
            variant="contained"
            disabled={!bulkDeleteReason.trim() || deletingBulk}
            startIcon={deletingBulk ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingBulk ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage User Challans Dialog */}
      <Dialog
        open={challansModalOpen}
        onClose={() => !deletingChallan && setChallansModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon color="success" />
          <Typography variant="h6" component="span" fontWeight="bold">
            Manage User Challans (Onsite)
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedStudentForChallans && (
            <Box mb={2} p={2} display="flex" flexWrap="wrap" gap={3} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Box flex="1 1 200px">
                <Typography variant="caption" color="textSecondary" display="block">User Name</Typography>
                <Typography variant="body2" fontWeight="500">{selectedStudentForChallans.fullName}</Typography>
              </Box>
              <Box flex="1 1 200px">
                <Typography variant="caption" color="textSecondary" display="block">Email Address</Typography>
                <Typography variant="body2" fontWeight="500">{selectedStudentForChallans.email}</Typography>
              </Box>
              <Box flex="1 1 200px">
                <Typography variant="caption" color="textSecondary" display="block">Roll Number</Typography>
                <Typography variant="body2" fontWeight="500">{selectedStudentForChallans.rollNumber || 'N/A'}</Typography>
              </Box>
            </Box>
          )}

          {loadingChallans ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress size={40} />
            </Box>
          ) : challansError ? (
            <Alert severity="error" sx={{ my: 2 }}>{challansError}</Alert>
          ) : userChallans.length === 0 ? (
            <Box py={5} textAlign="center" display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Typography variant="body1" color="textSecondary">
                No challans found for this onsite student.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PdfIcon />}
                disabled={generatingChallan}
                onClick={handleGenerateOnsiteChallan}
              >
                {generatingChallan ? "Generating..." : "Generate Challan"}
              </Button>
            </Box>
          ) : (
            <>
              {selectedChallanIds.length > 0 && (
                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center" p={1.5} sx={{ backgroundColor: '#ffebee', borderRadius: 1 }}>
                  <Typography color="error" variant="subtitle2" fontWeight="600">
                    {selectedChallanIds.length} challans selected
                  </Typography>
                  <Button
                    color="error"
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleBulkDeleteChallans}
                    disabled={deletingChallan}
                  >
                    Delete Selected
                  </Button>
                </Box>
              )}
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#fafafa' }}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedChallanIds.length > 0 && selectedChallanIds.length < userChallans.length}
                          checked={userChallans.length > 0 && selectedChallanIds.length === userChallans.length}
                          onChange={handleChallanSelectAll}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Challan ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>PSID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date Generated</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userChallans.map((challan) => {
                      const isItemSelected = selectedChallanIds.indexOf(challan._id) !== -1;
                      return (
                        <TableRow key={challan._id} hover selected={isItemSelected}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected}
                              onChange={() => handleChallanSelectOne(challan._id)}
                            />
                          </TableCell>
                          <TableCell>{challan.challanId}</TableCell>
                          <TableCell>{challan.psid}</TableCell>
                          <TableCell sx={{ fontWeight: '600' }}>Rs. {challan.fee}</TableCell>
                          <TableCell>
                            <Chip
                              icon={challan.status === 'paid' ? <VerifiedIcon /> : <CancelIcon />}
                              label={challan.status === 'paid' ? 'Paid' : 'Unpaid'}
                              color={challan.status === 'paid' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {challan.generatedAt ? new Date(challan.generatedAt).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" justifyContent="center" gap={1}>
                              <Tooltip title="Copy Challan Link">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleCopyChallanLink(challan)}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View PDF">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleViewChallanPDF(challan)}
                                >
                                  <PdfIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Challan">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleOpenDeleteChallan(challan)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', px: 3, py: 1.5 }}>
          <Button onClick={() => setChallansModalOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Challan Confirmation Dialog */}
      <Dialog
        open={deleteChallanConfirmOpen}
        onClose={() => !deletingChallan && setDeleteChallanConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Delete Onsite Challan
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete challan <strong>{selectedChallanToDelete?.challanId}</strong>?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1, mb: 2 }}>
              This will permanently delete the database record for this onsite challan.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteChallanConfirmOpen(false)} disabled={deletingChallan}>Cancel</Button>
          <Button
            onClick={handleDeleteChallanConfirm}
            color="error"
            variant="contained"
            disabled={deletingChallan}
            startIcon={deletingChallan ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingChallan ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog
        open={changePasswordModalOpen}
        onClose={() => !passwordChangeLoading && setChangePasswordModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKeyIcon color="warning" />
          Change Onsite User Password
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            {selectedStudentForAction && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>User:</strong> {selectedStudentForAction.fullName} ({selectedStudentForAction.email})
              </Alert>
            )}
            <TextField
              margin="dense"
              label="New Password"
              placeholder="Enter at least 8 characters..."
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              type="password"
              required
              disabled={passwordChangeLoading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Reason for Change (Logged)"
              placeholder="Explain why the password is being changed..."
              value={changePasswordReason}
              onChange={(e) => setChangePasswordReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              required
              disabled={passwordChangeLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordModalOpen(false)} disabled={passwordChangeLoading}>Cancel</Button>
          <Button
            onClick={confirmChangePassword}
            variant="contained"
            color="warning"
            disabled={!newPassword.trim() || newPassword.length < 8 || !changePasswordReason.trim() || passwordChangeLoading}
            startIcon={passwordChangeLoading ? <CircularProgress size={20} /> : <VpnKeyIcon />}
          >
            {passwordChangeLoading ? "Changing Password..." : "Change Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Name Modal */}
      <Dialog
        open={editNameModalOpen}
        onClose={() => !nameChangeLoading && setEditNameModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="info" />
          Edit Onsite Student Name
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            {selectedStudentForAction && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Current Name:</strong> {selectedStudentForAction.fullName} ({selectedStudentForAction.email})
              </Alert>
            )}
            <TextField
              margin="dense"
              label="New Name"
              placeholder="Enter new full name..."
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
              required
              disabled={nameChangeLoading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Reason for Change (Logged)"
              placeholder="Explain why the name is being changed..."
              value={editNameReason}
              onChange={(e) => setEditNameReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              required
              disabled={nameChangeLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNameModalOpen(false)} disabled={nameChangeLoading}>Cancel</Button>
          <Button
            onClick={confirmEditName}
            variant="contained"
            color="info"
            disabled={!newName.trim() || !editNameReason.trim() || nameChangeLoading}
            startIcon={nameChangeLoading ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {nameChangeLoading ? "Updating..." : "Update Name"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OnsiteUsers;

